import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function ApproveArtwork() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading, success, already_approved, error
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const approveArtwork = async () => {
      try {
        const response = await axios.get(`${API}/api/public/approve-artwork/${token}`);
        if (response.data.already_approved) {
          setStatus('already_approved');
        } else {
          setStatus('success');
        }
        setData(response.data);
      } catch (err) {
        setStatus('error');
        const detail = err.response?.data?.detail;
        const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to approve artwork. Please contact us directly.');
        setError(msg);
      }
    };

    if (token) {
      approveArtwork();
    }
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardContent className="p-8">
          {status === 'loading' && (
            <div className="text-center">
              <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-800">Processing your approval...</h2>
              <p className="text-gray-600 mt-2">Please wait a moment.</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-green-700 mb-2">Artwork Approved!</h2>
              <p className="text-gray-600 mb-4">
                Thank you, {data?.client_name}! Your memorial artwork for order <strong>#{data?.order_number}</strong> has been approved.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left">
                <h3 className="font-semibold text-green-800 mb-2">What happens next?</h3>
                <ul className="text-green-700 text-sm space-y-2">
                  <li>✓ We've been notified of your approval</li>
                  <li>✓ We'll order the stone and begin production</li>
                  <li>✓ We'll keep you updated on progress</li>
                  <li>✓ We'll contact you when it's ready for installation</li>
                </ul>
              </div>
              <p className="text-gray-500 text-sm mt-6">
                If you have any questions, please call us or email us.
              </p>
            </div>
          )}

          {status === 'already_approved' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-blue-700 mb-2">Already Approved</h2>
              <p className="text-gray-600 mb-4">
                This artwork for order <strong>#{data?.order_number}</strong> has already been approved.
              </p>
              <p className="text-gray-500 text-sm">
                Your memorial is in production. If you haven't heard from us recently, please get in touch.
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-12 h-12 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-red-700 mb-2">Something Went Wrong</h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <p className="text-gray-500 text-sm">
                Please contact us directly at <a href="mailto:memorials@setinstonedidcot.co.uk" className="text-blue-600 underline">memorials@setinstonedidcot.co.uk</a>
              </p>
            </div>
          )}

          <div className="mt-8 pt-6 border-t text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_headstone-orders/artifacts/1irdopez_email%20logo.png" 
              alt="Set In Stone Didcot Ltd" 
              className="h-12 mx-auto mb-2"
            />
            <p className="text-gray-500 text-sm">Set In Stone Didcot Ltd</p>
            <p className="text-gray-400 text-xs">Memorial Specialists</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default ApproveArtwork;
